/*       */

import React, { PureComponent } from 'react';
import { Button, CircularProgress } from '@mic3/platform-ui';
import { connect } from 'react-redux';
import genUuidv4 from 'uuid/v4';

import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { buildOrderComponents, formatByDefinitions, getSortOptions } from 'app/utils/filter/filterUtils';
import { addCustomOperators, buildCustomDefinitions } from 'app/utils/classification/classificationUtils';
import { clearFilterOperators, saveFilterOperators } from 'store/actions/filterbar/filterbarActions';
import { getAllFilterDefinitions, getDefaultFilterDefinitions, getDefaultFilters, getDefaultOrder } from '../dashboardUtils';

class AddListWidgetModal extends PureComponent {
    formRef = React.createRef();

    constructor(props) {
        super(props);

        const { entityTypes, widgetData } = props;
        const { entityType, header, total, order, filters, operatorsMap } = widgetData?.data || {};
        const typeData = entityTypes?.find((t) => t?.uri === entityType) || null;
        this.state = {
            selectedEntityType: typeData,
            formData: {
                widgetName: header,
                entityType: typeData,
                total,
                order: order || getDefaultOrder(entityType),
                filters: filters || getDefaultFilters(entityType),
            },
        };
        this.props.saveFilterOperators(operatorsMap);
    }

    componentWillUnmount() {
        this.props.clearFilterOperators();
    }

    @bind
    handleChange(formData, { name, value }) {
        const formState = { ...formData };
        if (name === 'entityType' && value) {
            formState.filters = { ...getDefaultFilters(value?.uri) }; // Clone filters object
            switch (value.uri) {
                case 'system_task':
                    formState.filters.statusType = null;
                    break;
                case 'closedtask':
                    formState.filters.statusType = 'closedtask';
                    break;
                case 'opentask':
                    formState.filters.statusType = 'opentask';
                    break;
                case 'system_process':
                    formState.filters.status = null;
                    break;
                case 'closedprocess':
                    formState.filters.status = 'is not null';
                    break;
                case 'openprocess':
                    formState.filters.status = 'is null';
                    break;
                default:
                    break;
            }
    
            formState.order = getDefaultOrder(value?.uri);
        }
        this.setState({ formData: formState });
    }

    @bind
    @memoize()
    getAllFilterDefs(selectedEntityType) {
        return getAllFilterDefinitions(selectedEntityType?.uri, selectedEntityType, {});
    }

    @bind
    @memoize()
    getFilterDefs(selectedEntityType) {
        return getDefaultFilterDefinitions(selectedEntityType?.uri, selectedEntityType, {}, true);
    }

    @bind
    @memoize()
    getCustomFilters(selectedEntityType) {
        return buildCustomDefinitions(selectedEntityType);
    }

    @bind
    @memoize()
    buildModalComponents(selectedEntityType) {
        const filterDefs = this.getFilterDefs(selectedEntityType);
        const allFilterDefs = this.getAllFilterDefs(selectedEntityType);
        const orderComponents = buildOrderComponents(allFilterDefs);

        return [
            {
                type: 'text',
                properties: { label: 'Widget Name', name: 'widgetName' },
                constraints: { required: true },
            },
            {
                type: 'entityTypesTypeahead',
                properties: {
                    name: 'entityType',
                    label: 'Entity Type',
                    extraQueryFields: [`formDefinition`],
                    placeholder: 'Search for an entity type...',
                    onChange: (evnt) => {
                        const selectedEntityType = evnt.target.value;
                        this.setState({ selectedEntityType });
                        return [evnt.target];
                    },
                },
                constraints: { required: true },
            },
            {
                type: 'number',
                properties: { label: 'Total Records', name: 'total', defaultValue: 10 },
                constraints: { required: true, onlyInteger: true, min: 1 },
            },
            selectedEntityType && {
                type: 'group',
                properties: { name: 'filters' },
                children: [
                    {
                        type: 'panel',
                        properties: { header: 'Filters', expanded: false },
                        children: filterDefs,
                    },
                ],
            },

            selectedEntityType && {
                type: 'group',
                properties: { name: 'order' },
                children: [
                    {
                        type: 'panel',
                        properties: { header: 'Sort By', expanded: false },
                        children: orderComponents,
                    },
                ],
            },
        ].filter(Boolean);
    }

    @bind
    onFormSubmit(event) {
        event.preventDefault();
        const { operatorsMap, onAddWidget, onClose, widgetData: widgetData, updateWidget } = this.props;
        const { id, type, data: existingData } = widgetData || {};
        const { selectedEntityType } = this.state;
        const filterDefinitions = this.getFilterDefs(selectedEntityType);
        const allFilterDefs = this.getAllFilterDefs(selectedEntityType);
        this.formRef.current.isValidForm().then(({ data, errors }) => {
            if (!errors) {
                const { field, direction } = data?.order || {};
                let order = [];
                const { filters } = data || {};
                let filterBy = formatByDefinitions(data?.filters, filterDefinitions);
                const userDefinedFilters = this.getCustomFilters(selectedEntityType);
                filterBy = addCustomOperators(filterDefinitions, filterBy, operatorsMap, userDefinedFilters);
                if (field) {
                    order = { field, direction: direction || 'desc nulls last' };
                }
                const sortDef = getSortOptions(allFilterDefs)?.find((def) => def?.value === field);
                const widgetData = {
                    header: data?.widgetName,
                    subHeader: sortDef?.label,
                    entityType: selectedEntityType?.uri,
                    filters,
                    filterBy,
                    order,
                    operatorsMap,
                    total: data?.total,
                };
                if (existingData) {
                    updateWidget({ data: widgetData, id, type });
                } else {
                    onAddWidget({ data: widgetData, id: genUuidv4(), type: 'list' }); // Widget would be added here
                }
                onClose();
            }
        });
    }

    render() {
        const { isLoading, onClose, widgetData } = this.props;
        const { selectedEntityType, formData } = this.state;
        const titlePrefix = widgetData?.data ? 'Edit' : 'Add';
        return (
            <ModalDialog
                title={`${titlePrefix} List Widget`}
                onClose={onClose}
                actions={isLoading ? <CircularProgress size={24} color='primary' /> : <Button onClick={this.onFormSubmit}>Apply</Button>}
            >
                <FormGenerator
                    components={this.buildModalComponents(selectedEntityType)}
                    ref={this.formRef}
                    data={formData}
                    onChange={this.handleChange}
                />
            </ModalDialog>
        );
    }
}

export default connect(
    (state) => ({
        operatorsMap: state.filterbar.operatorsMap,
        entityTypes: state.app.allPrimaryClasses.records || [],
    }),
    { clearFilterOperators, saveFilterOperators }
)(AddListWidgetModal);
